// @ts-nocheck
/**
 * CardDetailScreen - カード詳細画面
 *
 * カードの詳細情報をタブ形式で表示する
 */

import React, { useState, useMemo } from 'react';
import { useCardDex } from '../../hooks/useCardDex';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import {
  detailStyles,
  navStyles,
  COLORS,
} from '../../styles/cardDexStyles';
import { ATTRIBUTE_COLORS } from '../../../../utils/constants';
import { useCardData } from '../CardDexScreen';
import { RARITY_COLORS } from '../../../data/constants';
import {
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
} from '../../../merchant';
import { calculateCardValue } from '../../../systems/valueCalculator';
import { CardV2 } from '../../../../components/cardV2';
import MarketTab from './tabs/MarketTab';
import ObtainTab from './tabs/ObtainTab';
import DungeonTab from './tabs/DungeonTab';
import { isSetsunaMagic, getSetsunaCost } from '../../../../engine/keywordAbilities';

// ========================================
// ティア色定義
// ========================================

const TIER_COLORS = {
  S: '#ff6b6b',  // 赤
  A: '#ffa94d',  // オレンジ
  B: '#ffd43b',  // 黄
  C: '#69db7c',  // 緑
  D: '#868e96',  // グレー
};

// ========================================
// タブ定義
// ========================================

const DETAIL_TABS = [
  { id: 'basic', label: '基本情報' },
  { id: 'related', label: '関連カード' },
  { id: 'lore', label: '世界観' },
  { id: 'obtain', label: '入手情報' },
  { id: 'market', label: 'マーケット' },
  { id: 'dungeon', label: 'ダンジョン' },
];

// ========================================
// レアリティ切替
// ========================================

const RaritySwitcher = ({ selectedRarity, onRarityChange, ownedRarities }) => {
  const rarityList = ['C', 'UC', 'R', 'SR', 'UR', 'HR', 'SEC', 'ALT', 'SP', 'GR'];

  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '16px' }}>
      {rarityList.map((rarity) => {
        const isOwned = ownedRarities.includes(rarity);
        const isSelected = selectedRarity === rarity;
        const color = RARITY_COLORS[rarity] || '#888';

        return (
          <button
            key={rarity}
            onClick={() => onRarityChange(rarity)}
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: 'bold',
              borderRadius: '4px',
              border: isSelected ? `2px solid ${color}` : '2px solid transparent',
              background: isOwned
                ? `${color}33`
                : 'rgba(50, 50, 70, 0.5)',
              color: isOwned ? color : COLORS.textSecondary,
              cursor: 'pointer',
              opacity: isOwned ? 1 : 0.5,
              transition: 'all 0.2s ease',
            }}
          >
            {rarity}
          </button>
        );
      })}
    </div>
  );
};

// ========================================
// 基本情報タブ
// ========================================

const BasicInfoTab = ({ card, ownedInfo }) => {
  const attributeColor = ATTRIBUTE_COLORS[card.attribute] || {};

  // ティアを計算
  const cardValueInfo = calculateCardValue(card);
  const tier = cardValueInfo.tier;
  const tierColor = TIER_COLORS[tier] || TIER_COLORS.D;

  return (
    <div>
      {/* 基本ステータス */}
      <div style={detailStyles.statsRow}>
        <div style={detailStyles.stat}>
          属性: <span style={{ ...detailStyles.statValue, color: attributeColor.primary }}>{card.attribute}</span>
        </div>
        <div style={detailStyles.stat}>
          種類: <span style={detailStyles.statValue}>{card.type}</span>
        </div>
        <div style={detailStyles.stat}>
          コスト: <span style={detailStyles.statValue}>{card.cost}</span>
        </div>
        <div style={detailStyles.stat}>
          ティア: <span style={{
            ...detailStyles.statValue,
            color: tierColor,
            fontWeight: 'bold',
            textShadow: `0 0 8px ${tierColor}40`,
          }}>{tier}</span>
        </div>
      </div>

      {/* モンスターステータス */}
      {card.type === 'monster' && (
        <div style={detailStyles.statsRow}>
          <div style={detailStyles.stat}>
            ATK: <span style={{ ...detailStyles.statValue, color: COLORS.textDanger }}>{card.attack}</span>
          </div>
          <div style={detailStyles.stat}>
            HP: <span style={{ ...detailStyles.statValue, color: COLORS.textSuccess }}>{card.hp}</span>
          </div>
        </div>
      )}

      {/* カテゴリ */}
      {card.category && (
        <div style={{ marginTop: '12px' }}>
          <span style={detailStyles.stat}>カテゴリ: </span>
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              background: 'rgba(107, 76, 230, 0.3)',
              borderRadius: '4px',
              fontSize: '12px',
              color: COLORS.accent,
            }}
          >
            {card.category}
          </span>
        </div>
      )}

      {/* 刹那詠唱情報 */}
      {isSetsunaMagic(card) && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.2) 0%, rgba(255, 100, 100, 0.2) 100%)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 165, 0, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                background: 'linear-gradient(135deg, #ffa500 0%, #ff6b6b 100%)',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              刹那詠唱
            </span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              コスト: {getSetsunaCost(card)} SP
            </span>
          </div>
          <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.5 }}>
            相手ターンのバトルフェイズ開始時や攻撃宣言時に発動可能。
            チェーンとして割り込み発動できる魔法カード。
          </div>
        </div>
      )}

      {/* 効果テキスト */}
      {card.effect && (
        <div style={detailStyles.effectText}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: COLORS.textGold }}>
            【効果】
          </div>
          {card.effect}
        </div>
      )}

      {/* フレーバーテキスト */}
      {card.flavor && (
        <div
          style={{
            ...detailStyles.effectText,
            fontStyle: 'italic',
            color: COLORS.textSecondary,
          }}
        >
          {card.flavor}
        </div>
      )}

      {/* 所持情報 */}
      <div
        style={{
          marginTop: '20px',
          padding: '12px',
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
        }}
      >
        <div style={{ fontSize: '14px', color: COLORS.textSecondary, marginBottom: '8px' }}>
          所持状況
        </div>
        {ownedInfo.owned ? (
          <div>
            <span style={{ color: COLORS.textSuccess, fontWeight: 'bold' }}>
              所持済み
            </span>
            <span style={{ marginLeft: '12px', color: COLORS.textSecondary }}>
              レアリティ: {ownedInfo.rarities.join(', ')}
            </span>
          </div>
        ) : (
          <span style={{ color: COLORS.textSecondary }}>未所持</span>
        )}
      </div>
    </div>
  );
};

// ========================================
// 関連カードタブ
// ========================================

const RelatedCardsTab = ({ card, allCards, onCardClick }) => {
  // 同カテゴリのカード
  const sameCategoryCards = useMemo(() => {
    if (!card.category) return [];
    // category が文字列か配列かを考慮
    const cardCategory = typeof card.category === 'string'
      ? card.category
      : Array.isArray(card.category) ? card.category.join('') : '';
    if (!cardCategory) return [];
    const categoryKeyword = cardCategory.replace(/【|】/g, '');
    return allCards
      .filter((c) => {
        if (c.id === card.id || !c.category) return false;
        const cCategory = typeof c.category === 'string'
          ? c.category
          : Array.isArray(c.category) ? c.category.join('') : '';
        return cCategory.includes(categoryKeyword);
      })
      .slice(0, 6);
  }, [card, allCards]);

  // 同属性のカード
  const sameAttributeCards = useMemo(() => {
    return allCards
      .filter((c) => c.id !== card.id && c.attribute === card.attribute && c.type === card.type)
      .slice(0, 6);
  }, [card, allCards]);

  const RelatedCardItem = ({ relatedCard }) => (
    <div
      onClick={() => onCardClick(relatedCard.id)}
      style={{
        padding: '8px 12px',
        background: 'rgba(30, 30, 50, 0.6)',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ fontSize: '13px', color: COLORS.textPrimary }}>{relatedCard.name}</div>
      <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>
        {relatedCard.attribute} / コスト {relatedCard.cost}
      </div>
    </div>
  );

  return (
    <div>
      {/* 同カテゴリ */}
      {sameCategoryCards.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', color: COLORS.textGold, marginBottom: '12px' }}>
            ■ 同カテゴリ {card.category}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sameCategoryCards.map((c) => (
              <RelatedCardItem key={c.id} relatedCard={c} />
            ))}
          </div>
        </div>
      )}

      {/* 同属性 */}
      {sameAttributeCards.length > 0 && (
        <div>
          <div style={{ fontSize: '14px', color: COLORS.textGold, marginBottom: '12px' }}>
            ■ 同属性 {card.attribute}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sameAttributeCards.map((c) => (
              <RelatedCardItem key={c.id} relatedCard={c} />
            ))}
          </div>
        </div>
      )}

      {sameCategoryCards.length === 0 && sameAttributeCards.length === 0 && (
        <div style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '20px' }}>
          関連カードはありません
        </div>
      )}
    </div>
  );
};

// ========================================
// 世界観タブ
// ========================================

const LoreTab = ({ card }) => {
  return (
    <div>
      {/* フレーバーテキスト */}
      {card.flavor ? (
        <div
          style={{
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            fontStyle: 'italic',
            lineHeight: '1.8',
          }}
        >
          "{card.flavor}"
        </div>
      ) : (
        <div style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '20px' }}>
          世界観情報は準備中です
        </div>
      )}

      {/* カテゴリ情報 */}
      {card.category && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '14px', color: COLORS.textGold, marginBottom: '8px' }}>
            ■ カテゴリ情報
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
            {card.category} に属するカードです。
          </div>
        </div>
      )}
    </div>
  );
};


// ========================================
// メイン画面
// ========================================

const CardDetailScreen = ({ cardId }) => {
  const { selectedRarity, setSelectedRarity, goBack, navigateToDetail } = useCardDex();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('basic');

  // カードデータ取得
  const { allCards, collection, playerData, updatePlayerData } = useCardData();
  const card = useMemo(() => allCards.find((c) => c.id === cardId), [allCards, cardId]);
  const merchantData = playerData?.merchantData || {};

  // ウィッシュリスト状態
  const inWishlist = isInWishlist(merchantData, cardId);

  // ウィッシュリストトグル
  const handleToggleWishlist = () => {
    if (!updatePlayerData || !playerData) return;

    const newMerchantData = inWishlist
      ? removeFromWishlist(merchantData, cardId)
      : addToWishlist(merchantData, cardId);

    if (newMerchantData !== merchantData) {
      updatePlayerData({
        ...playerData,
        merchantData: newMerchantData,
      });
    }
  };

  // 所持情報取得
  const ownedInfo = useMemo(() => {
    const entries = collection.filter((entry) => entry.cardId === cardId);
    return {
      owned: entries.length > 0,
      rarities: entries.map((e) => e.rarity),
    };
  }, [cardId, collection]);

  if (!card) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
        カードが見つかりません
      </div>
    );
  }

  // タブコンテンツレンダリング
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return <BasicInfoTab card={card} ownedInfo={ownedInfo} />;
      case 'related':
        return (
          <RelatedCardsTab
            card={card}
            allCards={allCards}
            onCardClick={(id) => navigateToDetail(id)}
          />
        );
      case 'lore':
        return <LoreTab card={card} />;
      case 'obtain':
        return <ObtainTab card={card} allCards={allCards} />;
      case 'market':
        return (
          <MarketTab
            card={card}
            playerData={playerData}
            selectedRarity={selectedRarity}
          />
        );
      case 'dungeon':
        return <DungeonTab card={card} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        ...detailStyles.container,
        ...(isMobile ? detailStyles.containerMobile : {}),
      }}
    >
      {/* ヘッダー */}
      <div style={detailStyles.header}>
        {/* カード画像 */}
        <div
          style={{
            ...detailStyles.imageContainer,
            ...(isMobile ? detailStyles.imageMobile : {}),
            // CardV2のサイズに合わせて幅を調整
            width: isMobile ? 200 : 220,
            flexShrink: 0,
          }}
        >
          <CardV2
            card={card}
            displayMode="dex-list"
            displayWidth={isMobile ? 180 : 200}
            isOwned={true}
            showBottomName={false}
            showBottomCost={false}
            isMobile={isMobile}
          />
        </div>

        {/* 基本情報 */}
        <div style={detailStyles.infoContainer}>
          <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '4px' }}>
            {card.id}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h2
              style={{
                ...detailStyles.cardName,
                ...(isMobile ? detailStyles.cardNameMobile : {}),
                margin: 0,
              }}
            >
              {card.name}
            </h2>
            {/* ウィッシュリストボタン */}
            {updatePlayerData && (
              <button
                onClick={handleToggleWishlist}
                style={{
                  background: inWishlist ? 'rgba(255,100,100,0.8)' : 'rgba(60, 60, 80, 0.8)',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? '36px' : '40px',
                  height: isMobile ? '36px' : '40px',
                  fontSize: isMobile ? '18px' : '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
                title={inWishlist ? 'ウィッシュリストから削除' : 'ウィッシュリストに追加'}
              >
                {inWishlist ? '❤️' : '🤍'}
              </button>
            )}
          </div>

          {/* レアリティ切替 */}
          <RaritySwitcher
            selectedRarity={selectedRarity}
            onRarityChange={setSelectedRarity}
            ownedRarities={ownedInfo.rarities}
          />
        </div>
      </div>

      {/* タブナビゲーション */}
      <div style={{ ...navStyles.tabContainer, marginBottom: '0' }}>
        {DETAIL_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...navStyles.tab,
              ...(isMobile ? navStyles.tabMobile : {}),
              ...(activeTab === tab.id ? navStyles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      <div style={detailStyles.tabContent}>{renderTabContent()}</div>
    </div>
  );
};

export default CardDetailScreen;
